import React, { createContext, useContext, useState, useCallback } from 'react';
import { FiX, FiCheck, FiAlertCircle, FiInfo, FiAlertTriangle } from 'react-icons/fi';

const ToastContext = createContext(null);

const toastVariants = {
  success: {
    icon: FiCheck,
    bgColor: 'bg-success',
    borderColor: 'border-success',
    iconBg: 'bg-success/10',
    iconColor: 'text-success',
  },
  error: {
    icon: FiAlertCircle,
    bgColor: 'bg-danger',
    borderColor: 'border-danger',
    iconBg: 'bg-danger/10',
    iconColor: 'text-danger',
  },
  warning: {
    icon: FiAlertTriangle,
    bgColor: 'bg-warning',
    borderColor: 'border-warning',
    iconBg: 'bg-warning/10',
    iconColor: 'text-warning',
  },
  info: {
    icon: FiInfo,
    bgColor: 'bg-brand-500',
    borderColor: 'border-brand-500',
    iconBg: 'bg-brand-500/10',
    iconColor: 'text-brand-500',
  },
};

const ToastItem = ({ id, title, description, status = 'info', onClose }) => {
  const variant = toastVariants[status] || toastVariants.info;
  const Icon = variant.icon;

  return (
    <div
      className={`
        flex w-full max-w-md items-start gap-4 rounded-lg border-l-4 bg-white p-4 shadow-lg
        animate-slide-up dark:bg-boxdark
        ${variant.borderColor}
      `}
      role="alert"
    >
      <div className={`flex h-8 w-8 items-center justify-center rounded-full ${variant.iconBg}`}>
        <Icon className={variant.iconColor} size={18} />
      </div>

      <div className="flex-1">
        {title && (
          <h5 className="mb-1 font-medium text-black dark:text-white">
            {title}
          </h5>
        )}
        {description && (
          <p className="text-sm text-body dark:text-bodydark">
            {description}
          </p>
        )}
      </div>

      <button
        onClick={() => onClose(id)}
        className="text-body hover:text-black dark:text-bodydark dark:hover:text-white transition-colors"
      >
        <FiX size={18} />
      </button>
    </div>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback(({ title, description, status = 'info', duration = 5000 }) => {
    const id = Date.now() + Math.random();

    setToasts((prev) => [...prev, { id, title, description, status }]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const toast = useCallback((options) => {
    if (typeof options === 'string') {
      return addToast({ description: options });
    }
    return addToast(options);
  }, [addToast]);

  // Convenience methods
  toast.success = (options) => {
    if (typeof options === 'string') {
      return addToast({ description: options, status: 'success' });
    }
    return addToast({ ...options, status: 'success' });
  };

  toast.error = (options) => {
    if (typeof options === 'string') {
      return addToast({ description: options, status: 'error' });
    }
    return addToast({ ...options, status: 'error' });
  };

  toast.warning = (options) => {
    if (typeof options === 'string') {
      return addToast({ description: options, status: 'warning' });
    }
    return addToast({ ...options, status: 'warning' });
  };

  toast.info = (options) => {
    if (typeof options === 'string') {
      return addToast({ description: options, status: 'info' });
    }
    return addToast({ ...options, status: 'info' });
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}

      {/* Toast container */}
      <div className="fixed bottom-4 right-4 z-99999 flex flex-col gap-3">
        {toasts.map((toastItem) => (
          <ToastItem
            key={toastItem.id}
            {...toastItem}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export default ToastItem;
